import { useCallback, useEffect, useRef } from "react";

// Constants for CSS class selectors used in click detection
export const CSS_SELECTORS = {
  COLOR_PICKER: ".ColorPicker",
  COLOR_OPTIONS_CONTAINER: ".colorOptionsContainer",
  COLOR_CHOOSER_WRAPPER: ".colorChooserWrapper",
  BASE_MENU: ".BaseMenu",
  SKETCH_PICKER: ".sketch-picker",
  CUSTOM_COLOR_CONTAINER: ".customColorContainer"
} as const;

// Constants for test ID selectors
export const TEST_ID_SELECTORS = {
  COLOR_PICKER_BUTTON: '[data-testid="color-picker-button"]',
  SELECT_BUTTONS: '[data-testid^="select-"]',
  DEFAULT_COLOR_BUTTON: '[data-testid="default-color-button"]',
  RANGE_SLIDER_INPUT: '[data-testid="base-range-slider"]'
} as const;

// Global registry of close functions for all ColorPicker instances
const colorPickerCloseRegistry = new Map<string, () => void>();

// Registry for nested color chooser menus within each ColorPicker
const colorChooserCloseRegistry = new Map<string, Map<string, () => void>>();

// Global click handler to close all ColorPickers when clicking outside
const handleGlobalClick = (event: MouseEvent) => {
  const target = event.target as Element;

  // Check if the click is inside any ColorPicker or its related elements
  const isInsideColorPicker =
    target.closest(CSS_SELECTORS.COLOR_PICKER) ||
    target.closest(CSS_SELECTORS.COLOR_OPTIONS_CONTAINER) ||
    target.closest(TEST_ID_SELECTORS.COLOR_PICKER_BUTTON) ||
    target.closest(TEST_ID_SELECTORS.SELECT_BUTTONS) ||
    target.closest(TEST_ID_SELECTORS.DEFAULT_COLOR_BUTTON) || // ColorChooser color buttons
    target.closest(CSS_SELECTORS.COLOR_CHOOSER_WRAPPER) ||
    target.closest(CSS_SELECTORS.SKETCH_PICKER) || // Custom color picker
    target.closest(CSS_SELECTORS.CUSTOM_COLOR_CONTAINER) || // Custom color container
    target.closest(TEST_ID_SELECTORS.RANGE_SLIDER_INPUT);

  // Also check if the click is inside a BaseMenu that belongs to a ColorPicker
  const baseMenuElement = target.closest(CSS_SELECTORS.BASE_MENU);
  const isColorPickerMenu =
    baseMenuElement &&
    (baseMenuElement.querySelector(CSS_SELECTORS.COLOR_OPTIONS_CONTAINER) ||
      baseMenuElement.querySelector(CSS_SELECTORS.COLOR_CHOOSER_WRAPPER) ||
      baseMenuElement.closest(CSS_SELECTORS.COLOR_PICKER));

  // If the click is outside all ColorPickers and their menus, close them all
  if (!isInsideColorPicker && !isColorPickerMenu) {
    colorPickerCloseRegistry.forEach((closeFunction) => {
      closeFunction();
    });
  }
};

/**
 * Custom hook to manage exclusive ColorPicker state
 * Ensures only one ColorPicker can be open at a time across the entire application
 * Also ensures ColorPickers close when clicking outside any ColorPicker
 */
export function useExclusiveColorPicker() {
  const colorPickerIdRef = useRef<string>(Math.random().toString(36).substr(2, 9));

  // Function to close all other ColorPicker instances
  const closeOthers = useCallback(() => {
    colorPickerCloseRegistry.forEach((closeFunction, id) => {
      if (id !== colorPickerIdRef.current) {
        closeFunction();
      }
    });
  }, []);

  // Function to register the BaseMenu close function
  const registerCloseFunction = useCallback((closeFunction: () => void) => {
    colorPickerCloseRegistry.set(colorPickerIdRef.current, closeFunction);

    // Attach global click listener when first ColorPicker is registered
    if (colorPickerCloseRegistry.size === 1) {
      document.addEventListener("click", handleGlobalClick, true);
    }
  }, []);

  // Function to close other color choosers within this ColorPicker instance
  const closeOtherChoosers = useCallback((currentChooserId: string) => {
    const choosersMap = colorChooserCloseRegistry.get(colorPickerIdRef.current);
    if (choosersMap) {
      choosersMap.forEach((closeFunction, id) => {
        if (id !== currentChooserId) {
          closeFunction();
        }
      });
    }
  }, []);

  // Function to register a color chooser's close function
  const registerChooserCloseFunction = useCallback(
    (chooserId: string, closeFunction: () => void) => {
      if (!colorChooserCloseRegistry.has(colorPickerIdRef.current)) {
        colorChooserCloseRegistry.set(colorPickerIdRef.current, new Map());
      }
      const choosersMap = colorChooserCloseRegistry.get(colorPickerIdRef.current);
      if (choosersMap) {
        choosersMap.set(chooserId, closeFunction);
      }
    },
    []
  );

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      colorPickerCloseRegistry.delete(colorPickerIdRef.current);
      colorChooserCloseRegistry.delete(colorPickerIdRef.current);

      // Remove global click listener when last ColorPicker is unmounted
      if (colorPickerCloseRegistry.size === 0) {
        document.removeEventListener("click", handleGlobalClick, true);
      }
    };
  }, []);

  return {
    closeOthers,
    registerCloseFunction,
    closeOtherChoosers,
    registerChooserCloseFunction,
    colorPickerId: colorPickerIdRef.current
  };
}
